/**
 * API del microservicio DentalPro
 * Todas las URLs y llamadas al backend centralizadas y ordenadas por módulo.
 */
import api from './client';
import { getApiOrigin } from '../utils/apiBase';
import { cachedRequest, invalidateRequestCache } from '../utils/requestCache';

// =============================================================================
// AUTENTICACIÓN
// =============================================================================
export const loginAdmin = (email, password) =>
  api.post('/auth/login/admin', { email, password });

export const loginCustomer = (email, password) =>
  api.post('/auth/login/customer', { email, password });

export const register = (data) =>
  api.post('/auth/register', {
    email: data.email,
    password: data.password,
    full_name: data.nombre,
    phone: data.telefono || '',
  });

export const getMeAdmin = (accessToken) =>
  api.get('/auth/me/admin', accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {});

export const getMeCustomer = (accessToken) =>
  api.get('/auth/me/customer', accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {});
export const changePassword = (newPassword, currentPassword = null) =>
  api.post('/auth/change-password', {
    new_password: newPassword,
    ...(currentPassword ? { current_password: currentPassword } : {}),
  });
export const changePasswordAdmin = (newPassword, currentPassword = null) =>
  api.post('/auth/change-password/admin', {
    new_password: newPassword,
    ...(currentPassword ? { current_password: currentPassword } : {}),
  });

// =============================================================================
// PRODUCTOS
// =============================================================================
export const getProducts = (params = {}) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);

export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.patch(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

/** IVA global del catálogo (admin). */
export const getTaxConfig = () => api.get('/products/tax-config');
export const updateTaxConfig = (data) => api.put('/products/tax-config', data);

// Funciones de inventario (usando endpoint directo)
export const updateInventory = (productId, stockQuantity) => 
  api.patch(`/inventory/${productId}`, { stock_quantity: stockQuantity });

export const uploadProductImage = (productId, file, isPrimary = false) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_primary', isPrimary);
  return api.post(`/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteProductImage = (productId, imageId) =>
  api.delete(`/products/${productId}/images/${imageId}`);

// =============================================================================
// CATEGORÍAS
// =============================================================================
export const getCategories = (params = {}) => {
  const key = `categories:${JSON.stringify(params)}`;
  return cachedRequest(key, () => api.get('/categories', { params }), 5 * 60_000);
};
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.patch(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// =============================================================================
// CARRITO
// =============================================================================
export const getCart = () => api.get('/cart');
export const addToCart = (productId, quantity = 1) =>
  api.post('/cart/items', { product_id: productId, quantity });
export const updateCartItem = (itemId, quantity) =>
  api.patch(`/cart/items/${itemId}`, null, { params: { quantity } });
export const removeFromCart = (itemId) => api.delete(`/cart/items/${itemId}`);
export const clearCart = () => api.delete('/cart');

// =============================================================================
// ÓRDENES
// =============================================================================
export const createOrder = (data, { idempotencyKey } = {}) =>
  api.post('/orders', data, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
export const getOrders = (statusFilter) => {
  const params = statusFilter ? { status_filter: statusFilter } : {};
  return api.get('/orders', { params });
};
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrderStatus = (id, status, statusNotes) =>
  api.patch(`/orders/${id}`, { status, status_notes: statusNotes || null });

// =============================================================================
// FACTURACIÓN (registro automático al confirmar pedido en el e-commerce)
// =============================================================================
export const getInvoices = (params = {}) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const getInvoiceByOrder = (orderId) => api.get(`/invoices/by-order/${orderId}`);

// Facturación electrónica (Factuplan)
export const getBillingConfig = () => api.get('/billing/config');
export const updateBillingConfig = (data) => api.put('/billing/config', data);
export const listFactuplanProducts = (search) =>
  api.get('/billing/products', { params: search ? { search } : {} });
export const createFactuplanProduct = (data) => api.post('/billing/products', data);
export const updateFactuplanProduct = (externalId, data) =>
  api.patch(`/billing/products/external/${externalId}`, data);
export const deleteFactuplanProduct = (externalId) =>
  api.delete(`/billing/products/external/${externalId}`);
export const syncProductToFactuplan = (productId) =>
  api.post(`/billing/products/sync/${productId}`);
export const syncAllProductsToFactuplan = () => api.post('/billing/products/sync-all');
export const getBillingProductLinks = () => api.get('/billing/products/links');
export const emitInvoiceToFactuplan = (invoiceId, deductInventory = true) =>
  api.post(`/billing/invoices/${invoiceId}/emit`, null, {
    params: { deduct_inventory: deductInventory },
  });

// =============================================================================
// EMPRESA / CONTACTO
// =============================================================================
export const getCompanyInfo = () =>
  cachedRequest('company', () => api.get('/company'), 5 * 60_000);
export const updateCompanyInfo = (data) =>
  api.patch('/company', data).then((res) => {
    invalidateRequestCache('company');
    return res;
  });
export const submitContactForm = (data) => api.post('/company/contact', data);

// =============================================================================
// NEWSLETTER
// =============================================================================
export const subscribeNewsletter = (data) => api.post('/newsletter/subscribe', data);
export const getNewsletterSubscribers = (params) => api.get('/newsletter/admin/subscribers', { params });
export const unsubscribeNewsletterSubscriber = (id) =>
  api.patch(`/newsletter/admin/subscribers/${id}/unsubscribe`);
export const getNewsletterCampaigns = (params) => api.get('/newsletter/admin/campaigns', { params });
export const sendNewsletterCampaign = (data) => api.post('/newsletter/admin/campaigns/send', data);

export const uploadCompanyLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/company/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  return `${getApiOrigin()}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
};

// =============================================================================
// CARRITOS ABANDONADOS
// =============================================================================
export const getAbandonedCarts = (params) => api.get('/abandoned-carts', { params });
export const syncAbandonedCarts = () => api.post('/abandoned-carts/sync');
export const markAbandonedCartRecovered = (id) => api.patch(`/abandoned-carts/${id}/recover`);

// =============================================================================
// USUARIOS ADMIN
// =============================================================================
export const listAdminUsers = () => api.get('/admin-users');
export const createAdminUser = (data) => api.post('/admin-users', data);
export const updateAdminUser = (id, data) => api.patch(`/admin-users/${id}`, data);

// =============================================================================
// CONSULTORIOS (TENANTS)
// =============================================================================
export const listTenants = () => api.get('/tenants');
export const createTenant = (data) => api.post('/tenants', data);
export const updateTenant = (id, data) => api.patch(`/tenants/${id}`, data);
export const deleteTenant = (id) => api.delete(`/tenants/${id}`);
export const getTenantBillingConfig = (tenantId) => api.get(`/tenants/${tenantId}/billing-config`);

// =============================================================================
// USUARIOS CONSULTORIO
// =============================================================================
export const searchCustomers = (q, tenantId) => {
  const params = {};
  if (q) params.q = q;
  if (tenantId) params.tenant_id = tenantId;
  return api.get('/consultorio-users/customers-search', { params });
};
export const listConsultorioUsers = (tenantId) => api.get(`/consultorio-users/tenant/${tenantId}`);
export const addConsultorioUser = (tenantId, data) => api.post(`/consultorio-users/tenant/${tenantId}`, data);
export const createAndAddConsultorioUser = (tenantId, data) =>
  api.post(`/consultorio-users/tenant/${tenantId}/create-and-add`, data);
export const updateConsultorioUser = (id, data) => api.patch(`/consultorio-users/${id}`, data);
export const removeConsultorioUser = (id) => api.delete(`/consultorio-users/${id}`);
export const sendConsultorioUserCredentials = (id, data = {}) =>
  api.post(`/consultorio-users/${id}/send-credentials`, data);

// Consultorio admin (cuando el usuario es administrador del consultorio, usa token de customer)
export const getConsultorioAdminMe = () => api.get('/consultorio-admin/me');
export const listConsultorioAdminUsers = () => api.get('/consultorio-admin/users');
export const searchConsultorioAdminCustomers = (q) => api.get('/consultorio-admin/customers-search', { params: { q } });
export const addConsultorioAdminUser = (data) => api.post('/consultorio-admin/users', data);
export const createAndAddConsultorioAdminUser = (data) =>
  api.post('/consultorio-admin/users/create-and-add', data);
export const updateConsultorioAdminUser = (id, data) => api.patch(`/consultorio-admin/users/${id}`, data);
export const removeConsultorioAdminUser = (id) => api.delete(`/consultorio-admin/users/${id}`);
export const sendConsultorioAdminUserCredentials = (id, data = {}) =>
  api.post(`/consultorio-admin/users/${id}/send-credentials`, data);

/** Miembros activos del mismo tenant (token de customer consultorio). */
export const listConsultorioMembers = () => api.get('/consultorio-members');

/** Perfil profesional MSP del usuario del consultorio (firma, código, etc.). */
export const getConsultorioProfile = () => api.get('/consultorio-profile/me');
export const updateConsultorioProfile = (data) => api.patch('/consultorio-profile/me', data);

// =============================================================================
// VOZ (Whisper en backend — no usa Google Chrome Speech)
// =============================================================================
export const getSpeechStatus = () => api.get('/speech/status');
export const transcribeSpeechAudio = (blob, filename = 'comando.webm') => {
  const formData = new FormData();
  formData.append('file', blob, filename);
  return api.post('/speech/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};

// =============================================================================
// DASHBOARD
// =============================================================================
export const getDashboardStats = () => api.get('/dashboard/stats');

export const getAdminNotifications = () => api.get('/dashboard/notifications');

// =============================================================================
// LOGS DE AUDITORÍA
// =============================================================================
export const getAuditLogs = (params = {}) => api.get('/audit-logs', { params });
